import React, { useCallback, useEffect, useState } from 'react';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import { Alert, Box, Button, Stack, Typography } from '@mui/material';
import ApiService from '../services/api';
import RouteLoadingGate from './RouteLoadingGate';
import { hasMsmeExportIntent } from '../utils/msmeExportIntent';

interface RequireExportIntentProps {
  children: React.ReactNode;
}

/**
 * Route gate for export-only surfaces (/compliance/export, /buyer-audit).
 * Sidebar already hides these without export intent; direct URLs must not bypass that (DEEP-BUG-011).
 */
const RequireExportIntent: React.FC<RequireExportIntentProps> = ({ children }) => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [allowed, setAllowed] = useState(false);
  const [loadFailed, setLoadFailed] = useState(false);

  const checkExportIntent = useCallback(async () => {
    setLoadFailed(false);
    try {
      const response = await ApiService.getMSMEProfile();
      const profile = response?.data;
      setAllowed(hasMsmeExportIntent(profile as Parameters<typeof hasMsmeExportIntent>[0]));
    } catch {
      setAllowed(false);
      setLoadFailed(true);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void checkExportIntent();
  }, [checkExportIntent]);

  if (loading) {
    return (
      <RouteLoadingGate message="Checking export workspace access…" minHeight="400px" />
    );
  }

  if (loadFailed) {
    return (
      <Box sx={{ p: 4, maxWidth: 480, mx: 'auto' }}>
        <Alert severity="warning" sx={{ mb: 2 }}>
          We could not verify export preferences. Please try again in a moment.
        </Alert>
        <Stack direction="row" spacing={1}>
          <Button
            variant="contained"
            onClick={() => {
              setLoading(true);
              void checkExportIntent();
            }}
          >
            Retry
          </Button>
          <Button variant="text" onClick={() => navigate('/dashboard')}>
            Dashboard
          </Button>
        </Stack>
      </Box>
    );
  }

  if (!allowed) {
    return (
      <Box sx={{ p: 3, maxWidth: 640, mx: 'auto' }}>
        <Typography component="h1" variant="h6" sx={{ mb: 1 }}>
          Buyer audit pack needs export activity
        </Typography>
        <Alert severity="info" sx={{ mb: 2 }}>
          Export compliance and buyer-audit tools unlock after you confirm export activity in your
          company profile. This is not a missing page — the pack is gated until export intent is on.
        </Alert>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          If your business exports goods or services, turn on export activity under company profile,
          then return here.
        </Typography>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
          <Button component={RouterLink} to="/compliance" variant="contained">
            Open compliance hub
          </Button>
          <Button component={RouterLink} to="/msme-profile" variant="outlined">
            Company profile
          </Button>
          <Button onClick={() => navigate('/dashboard')} variant="text">
            Dashboard
          </Button>
        </Stack>
      </Box>
    );
  }

  return <>{children}</>;
};

export default RequireExportIntent;
